import { useEffect, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  BadgeDollarSign,
  Bell,
  Camera,
  Check,
  CheckCircle2,
  CircleHelp,
  CircleMinus,
  ClipboardList,
  FileUp,
  FolderOpen,
  LayoutDashboard,
  ListChecks,
  MessageSquareText,
  Moon,
  Search,
  Share2,
  Smartphone,
  Sun,
  Users,
} from 'lucide-react';

import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/shared/ui/dialog';
import { cn } from '@/shared/utils/utils';

import {
  IRONVIEW_LOGO_DARK,
  IRONVIEW_LOGO_LIGHT,
  PROCORE_LOGO_WHITE,
} from './brandAssets';
import './services-announcement.css';

type Theme = 'dark' | 'light';

interface CoreFeature {
  label: string;
  icon: LucideIcon;
}

interface ComparisonRow {
  category: string;
  icon: LucideIcon;
  ironviewTitle: string;
  ironviewDetail: string;
  procoreTitle: string;
  procoreDetail: string;
  procorePositive?: boolean;
}

const coreFeatures: CoreFeature[] = [
  { label: 'Drawings', icon: LayoutDashboard },
  { label: 'Punch Lists', icon: ListChecks },
  { label: 'Observations', icon: MessageSquareText },
  { label: 'RFIs', icon: CircleHelp },
  { label: 'Submittals', icon: FileUp },
  { label: 'Files', icon: FolderOpen },
  { label: 'Daily Log', icon: ClipboardList },
];

const comparisonRows: ComparisonRow[] = [
  {
    category: 'Visual documentation',
    icon: Camera,
    ironviewTitle: 'Built around reality capture',
    ironviewDetail: '360° captures, drawing overlays, and fast visual lookup.',
    procoreTitle: 'Project photos and records',
    procoreDetail: 'No dedicated full-service 360° capture program.',
  },
  {
    category: 'Project management',
    icon: ClipboardList,
    ironviewTitle: 'Core workflows included',
    ironviewDetail: 'Drawings, Punch Lists, Observations, RFIs, Submittals, Files, and Daily Log.',
    procoreTitle: 'Deep management workflows',
    procoreDetail: 'Strong tools for project controls and compliance.',
    procorePositive: true,
  },
  {
    category: 'Field access',
    icon: Smartphone,
    ironviewTitle: 'Made for the field',
    ironviewDetail: 'Capture, find, and act in seconds from any device.',
    procoreTitle: 'Full mobile toolkit',
    procoreDetail: 'A broad platform with more setup and configuration.',
    procorePositive: true,
  },
  {
    category: 'Sharing',
    icon: Share2,
    ironviewTitle: 'Share and export fast',
    ironviewDetail: 'Links, JPEGs, and reports keep teams and trades aligned.',
    procoreTitle: 'Permission-based sharing',
    procoreDetail: 'Access is managed across the broader project structure.',
    procorePositive: true,
  },
  {
    category: 'Value',
    icon: BadgeDollarSign,
    ironviewTitle: 'One focused service',
    ironviewDetail: 'Reality capture and core workflows in one connected workspace.',
    procoreTitle: 'Broad enterprise platform',
    procoreDetail: 'Pricing, modules, and add-ons vary by configuration.',
  },
];

const IronviewLogo = ({ theme, className }: { theme: Theme; className?: string }) => (
  <img
    src={
      theme === 'dark'
        ? IRONVIEW_LOGO_DARK
        : IRONVIEW_LOGO_LIGHT
    }
    alt="Ironview 360"
    className={cn('w-auto object-contain', className)}
  />
);

const ProcoreLogo = ({ className }: { className?: string }) => (
  <span className={cn('inline-flex rounded-[9px] bg-[#141414] px-3 py-2', className)}>
    <img
      src={PROCORE_LOGO_WHITE}
      alt="Procore"
      className="h-[18px] w-auto object-contain"
    />
  </span>
);

const ComparisonStatus = ({ positive = false }: { positive?: boolean }) => {
  const Icon = positive ? CheckCircle2 : CircleMinus;
  return (
    <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground">
      <Icon className="size-4" strokeWidth={1.9} />
    </span>
  );
};

function PreviewDashboard({ theme }: { theme: Theme }) {
  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[17rem_1fr]">
      <aside className="hidden border-r border-border bg-card/85 px-5 py-6 lg:block">
        <IronviewLogo theme={theme} className="h-7" />
        <div className="mt-10 space-y-7">
          {['General', 'Ironview 360', 'Construction management'].map((group, groupIndex) => (
            <div key={group}>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                {group}
              </div>
              <div className="mt-3 space-y-2">
                {[0, 1, 2].map((item) => (
                  <div
                    key={item}
                    className={cn(
                      'h-9 rounded-[10px] border border-border bg-muted/70',
                      groupIndex === 0 && item === 0 && 'border-primary/30 bg-primary/10'
                    )}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </aside>
      <main className="min-w-0">
        <header className="flex items-center gap-3 border-b border-border bg-background/80 px-6 py-4 backdrop-blur-xl">
          <div className="flex h-10 max-w-[390px] flex-1 items-center gap-2.5 rounded-xl border border-border bg-card px-3.5 text-muted-foreground">
            <Search className="size-4" strokeWidth={1.9} />
            <span className="text-sm">Search projects, drawings, RFIs…</span>
          </div>
          <div className="flex-1" />
          <div className="grid size-10 place-items-center rounded-[11px] border border-border bg-card text-muted-foreground">
            <Bell className="size-5" strokeWidth={1.9} />
          </div>
          <div className="flex h-10 items-center gap-2 rounded-full border border-border bg-card px-3 text-sm font-semibold">
            <Users className="size-4 text-primary" strokeWidth={1.9} /> Admin
          </div>
        </header>
        <div className="mx-auto max-w-[1400px] p-6 md:p-9">
          <div className="h-4 w-36 rounded bg-muted" />
          <div className="mt-3 h-10 w-64 rounded-lg bg-card" />
          <div className="mt-7 grid gap-5 md:grid-cols-3">
            {[0, 1, 2].map((item) => (
              <div key={item} className="h-36 rounded-[1.375rem] border border-border bg-card shadow-slate" />
            ))}
          </div>
          <div className="mt-5 h-[420px] rounded-[1.375rem] border border-border bg-card shadow-slate" />
        </div>
      </main>
    </div>
  );
}

interface ServicesAnnouncementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  theme: Theme;
  onThemeToggle?: () => void;
}

export function ServicesAnnouncementDialog({
  open,
  onOpenChange,
  theme,
  onThemeToggle,
}: ServicesAnnouncementDialogProps) {
  const previewThemeClass = cn('services-preview-theme', theme === 'dark' && 'dark');

  return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            previewThemeClass,
            'max-h-[92vh] max-w-[1180px] gap-0 overflow-hidden border-border bg-card p-0 text-card-foreground'
          )}
          closeButtonClassName="right-4 top-4 border-border bg-muted text-foreground hover:bg-accent focus:ring-ring"
        >
          <div className="flex max-h-[92vh] min-h-0 flex-col">
            <div className="services-preview-scroll min-h-0 flex-1 overflow-y-auto">
              <header className="services-preview-glow border-b border-border px-5 pb-5 pt-6 sm:px-7 sm:pb-6 sm:pt-7 lg:px-9 lg:pb-7">
                <div className="flex items-center justify-between gap-4 pr-11">
                  <IronviewLogo theme={theme} className="h-7 sm:h-8" />
                  {onThemeToggle && (
                    <Button
                      variant="outline"
                      size="icon"
                      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
                      onClick={onThemeToggle}
                    >
                      {theme === 'dark' ? <Sun /> : <Moon />}
                    </Button>
                  )}
                </div>

                <div className="mt-7">
                  <DialogTitle className="iv-display max-w-[940px] text-[2rem] leading-[1.08] sm:text-[2.65rem] lg:text-[3rem]">
                    Capture the site. Run the project.{' '}
                    <span className="text-primary">One connected workspace.</span>
                  </DialogTitle>
                  <DialogDescription className="mt-4 max-w-[820px] text-[15px] leading-6 sm:text-base">
                    Ironview 360 now brings 360° visual documentation and seven core field workflows
                    into one calm, easy-to-navigate platform.
                  </DialogDescription>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
                  {coreFeatures.map(({ label, icon: Icon }) => (
                    <div
                      key={label}
                      className="flex items-center gap-2 rounded-[11px] border border-border bg-muted px-3 py-2.5 text-[12.5px] font-semibold"
                    >
                      <Icon className="size-4 shrink-0 text-primary" strokeWidth={1.9} />
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              </header>

              <section className="bg-background/45 px-5 py-6 sm:px-7 lg:px-9 lg:py-7" aria-labelledby="comparison-title">
                <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                      Platform comparison
                    </div>
                    <h2 id="comparison-title" className="iv-display mt-1 text-xl font-extrabold tracking-[-0.02em] sm:text-2xl">
                      See what is different at a glance
                    </h2>
                  </div>
                  <span className="text-xs font-medium text-muted-foreground">Built for the field, without the clutter.</span>
                </div>

                <div className="hidden overflow-hidden rounded-[1.375rem] border border-border bg-card shadow-slate md:block">
                  <table className="w-full table-fixed border-collapse text-left">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="w-[22%] px-5 py-4 text-[11px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
                          Capability
                        </th>
                        <th className="w-[39%] border-l border-border bg-primary/[0.06] px-5 py-4">
                          <IronviewLogo theme={theme} className="h-6" />
                        </th>
                        <th className="w-[39%] border-l border-border px-5 py-3.5">
                          <ProcoreLogo />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonRows.map((row) => {
                        const CategoryIcon = row.icon;
                        return (
                          <tr key={row.category} className="border-b border-border last:border-b-0">
                            <th scope="row" className="px-5 py-4 align-top">
                              <div className="flex items-center gap-2.5">
                                <span className="grid size-9 shrink-0 place-items-center rounded-[10px] border border-border bg-muted text-primary">
                                  <CategoryIcon className="size-4" strokeWidth={1.9} />
                                </span>
                                <span className="text-[13px] font-bold leading-4">{row.category}</span>
                              </div>
                            </th>
                            <td className="border-l border-border bg-primary/[0.035] px-5 py-4 align-top">
                              <div className="flex gap-3">
                                <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
                                  <Check className="size-4" strokeWidth={2.2} />
                                </span>
                                <div>
                                  <div className="text-[13.5px] font-bold text-primary">{row.ironviewTitle}</div>
                                  <div className="mt-1 text-[12.5px] leading-[1.45] text-muted-foreground">{row.ironviewDetail}</div>
                                </div>
                              </div>
                            </td>
                            <td className="border-l border-border px-5 py-4 align-top">
                              <div className="flex gap-3">
                                <ComparisonStatus positive={row.procorePositive} />
                                <div>
                                  <div className="text-[13.5px] font-bold">{row.procoreTitle}</div>
                                  <div className="mt-1 text-[12.5px] leading-[1.45] text-muted-foreground">{row.procoreDetail}</div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="space-y-3 md:hidden">
                  {comparisonRows.map((row) => {
                    const CategoryIcon = row.icon;
                    return (
                      <div key={row.category} className="overflow-hidden rounded-[1.375rem] border border-border bg-card shadow-slate">
                        <div className="flex items-center gap-2.5 border-b border-border px-4 py-3.5">
                          <span className="grid size-8 place-items-center rounded-[9px] bg-muted text-primary">
                            <CategoryIcon className="size-4" strokeWidth={1.9} />
                          </span>
                          <h3 className="text-[13px] font-bold">{row.category}</h3>
                        </div>
                        <div className="space-y-4 px-4 py-4">
                          <div className="flex gap-3">
                            <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
                              <Check className="size-4" strokeWidth={2.2} />
                            </span>
                            <div>
                              <IronviewLogo theme={theme} className="mb-2 h-5" />
                              <div className="text-sm font-bold text-primary">{row.ironviewTitle}</div>
                              <div className="mt-1 text-[13px] leading-5 text-muted-foreground">{row.ironviewDetail}</div>
                            </div>
                          </div>
                          <div className="h-px bg-border" />
                          <div className="flex gap-3">
                            <ComparisonStatus positive={row.procorePositive} />
                            <div>
                              <ProcoreLogo className="mb-2" />
                              <div className="text-sm font-bold">{row.procoreTitle}</div>
                              <div className="mt-1 text-[13px] leading-5 text-muted-foreground">{row.procoreDetail}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>

            <footer className="flex flex-col gap-4 border-t border-border bg-card px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7 lg:px-9">
              <div>
                <div className="iv-display text-sm font-bold">Ready to see behind the walls?</div>
                <div className="mt-0.5 text-xs text-muted-foreground">Comparison may vary by plan and configuration.</div>
              </div>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>Got It</Button>
            </footer>
          </div>
        </DialogContent>
      </Dialog>
  );
}

function ServicesAnnouncementPreview() {
  const [open, setOpen] = useState(true);
  const [theme, setTheme] = useState<Theme>('dark');

  useEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains('dark');
    root.classList.toggle('dark', theme === 'dark');

    return () => {
      root.classList.toggle('dark', wasDark);
    };
  }, [theme]);

  const previewThemeClass = cn('services-preview-theme', theme === 'dark' && 'dark');

  return (
    <div className={cn(previewThemeClass, 'services-preview-glow relative min-h-screen bg-background text-foreground')}>
      <PreviewDashboard theme={theme} />

      <div className="fixed bottom-5 right-5 z-10 flex items-center gap-2 rounded-[13px] border border-border bg-card p-1.5 shadow-slate">
        <span className="pl-3 text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
          Concept preview
        </span>
        <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
          Reopen popup
        </Button>
      </div>

      <ServicesAnnouncementDialog
        open={open}
        onOpenChange={setOpen}
        theme={theme}
        onThemeToggle={() => setTheme((current) => (current === 'dark' ? 'light' : 'dark'))}
      />
    </div>
  );
}

export default ServicesAnnouncementPreview;
